"use client";

import { size, theme } from "@/config/theme";
import { SCButton, SCContainerContent } from "@/utils/styledComponents";
import { useTranslations } from "next-intl";
import styled from "styled-components";
import PlanAndPriceItem from "./Component-PlanAndPriceItem";
import { EmblaOptionsType } from "embla-carousel";
import EmblaCarousel from "./Component-EmblaCarousel-PlansAndPrices";
import { breakpoints } from "@/utils/breakpoint";
import { appLocale, appSelectedCountry } from "@/recoil/appState";
import { useRecoilValue } from "recoil";
import PlanAndPriceItemForId from "./Component-PlanAndPriceItemForId";
import EmblaCarouselForId from "./Component-EmblaCarousel-PlansAndPricesForId";
import { useRouter } from "next/navigation";
import { canonicalPath } from "@/utils/canonicalPath";

const plansAndPriceList = [
    {
        plan: "basic",
        name: "Silver Package",
        price1: "$240",
        price2: " / 8 week term",
        detailList: [
            "1 class per week",
            "45 minutes each",
            "No make-up classes"
        ],
        popular: false
    },
    {
        plan: "premium",
        name: "Gold Package",
        price1: "$300",
        price2: " / 8 week term",
        detailList: [
            "1 class per week",
            "45 minutes each",
            "Make-up classes available"
        ],
        popular: true
    },
    {
        plan: "unlimited",
        name: "Diamond (Recommended for expat)",
        price1: "$600",
        price2: " / 8 week term",
        detailList: ["No limit on classes"],
        popular: false
    }
];

const plansAndPriceListForId = [
    {
        plan: "id8weeks",
        name: "8 Weeks Plan",
        price1: { region: "Jakarta, Medan, Surabaya", amount: "1,000,000 IDR" },
        price2: { region: "Bali, Bandung", amount: "750,000 IDR" },
        detailList: [
            "1 class per week",
            "45 minutes each",
            "Student progress report",
            "Consultation with coach"
        ],
        popular: false
    },
    {
        plan: "id16weeks",
        name: "16 Weeks Plan",
        price1: { region: "Jakarta, Medan, Surabaya", amount: "2,000,000 IDR" },
        price2: { region: "Bali, Bandung", amount: "1,500,000 IDR" },
        detailList: [
            "1 class per week",
            "45 minutes each",
            "Student progress report",
            "Consultation with coach"
        ],
        popular: false
    },
    {
        plan: "id24weeks",
        name: "24 Weeks Plan",
        price1: { region: "Jakarta, Medan, Surabaya", amount: "3,000,000 IDR" },
        price2: { region: "Bali, Bandung", amount: "2,250,000 IDR" },
        detailList: [
            "1 class per week",
            "45 minutes each",
            "Student progress report",
            "Consultation with coach",
            "Bonus swim cap",
            "Priority customer service"
        ],
        popular: false
    }
];

const PlansAndPrices = () => {
    const t = useTranslations("PlansAndPrices");
    const selectedCountry = useRecoilValue(appSelectedCountry);
    const router = useRouter();
    const localeAppState = useRecoilValue(appLocale);

    const OPTIONS: EmblaOptionsType = { loop: true };
    const SLIDES_SG = plansAndPriceList;
    const SLIDES_ID = plansAndPriceListForId;

    return (
        <SCPlansAndPrices>
            <SCPlansAndPricesContent>
                <h2 className="text-largexlarge">{t("title-text")}</h2>
                {selectedCountry === "sg" && (
                    <>
                        <div className="item-container non-mobile-view">
                            {plansAndPriceList.map((item, index) => (
                                <PlanAndPriceItem
                                    key={index}
                                    item={{ ...item, ismobile: false }}
                                />
                            ))}
                        </div>
                        <div className="mobile-view">
                            <EmblaCarousel
                                slides={SLIDES_SG}
                                options={OPTIONS}
                            />
                        </div>
                    </>
                )}
                {selectedCountry === "id" && (
                    <>
                        <div className="item-container non-mobile-view for-id">
                            {plansAndPriceListForId.map((item, index) => (
                                <PlanAndPriceItemForId
                                    key={index}
                                    item={item}
                                />
                            ))}
                        </div>
                        <div className="mobile-view for-id">
                            <EmblaCarouselForId
                                slides={SLIDES_ID}
                                options={OPTIONS}
                            />
                        </div>
                    </>
                )}
                <div className="subtext-container">
                    <div className="text-medium">{t("description-text")}</div>
                    <div>
                        <SCBookTrialButton
                            onClick={() => {
                                router.push(
                                    canonicalPath(
                                        `/${localeAppState}/book-trial`
                                    )
                                );
                            }}
                        >
                            {t("Book Free Trial")}
                        </SCBookTrialButton>
                    </div>
                </div>
            </SCPlansAndPricesContent>
        </SCPlansAndPrices>
    );

    return "Country not selected.";
};

export default PlansAndPrices;

const SCPlansAndPrices = styled.div`
    flex-direction: row;
    justify-content: center;
    padding: ${theme["spacing-lxl"]} 0;
    background-image: url("/media/images/background-plansandprices.webp");
    background-size: cover;
    background-repeat: no-repeat;
    display: flex;
`;

const SCPlansAndPricesContent = styled(SCContainerContent)`
    display: flex;
    flex-direction: column;

    .text-largexlarge {
        font-size: ${theme["fontsize-lxl"]};
        font-weight: 500;
    }

    .text-medium {
        font-size: ${theme["fontsize-m"]};
        color: ${theme["color-grey"]};
    }

    .item-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        .item {
            background: ${theme["color-white"]};
            padding: ${theme["spacing-l"]};
            // flex: 1;
            width: 30%;
            margin-right: ${theme["spacing-l"]};
            height: 280px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            &:last-item {
                margin-right: 0;
            }

            &.basic {
                .plan {
                    color: ${theme["color-blue"]};
                    margin-bottom: ${theme["spacing-sm"]};
                }
            }

            &.premium {
                border: 1px solid ${theme["color-price-yellow"]};
                padding-top: 0;
                justify-content: normal;
                align-items: flex-end;

                .premium {
                    flex: 1;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .plan {
                    color: ${theme["color-price-yellow"]};
                    margin-bottom: ${theme["spacing-sm"]};
                }

                .popular {
                    background-color: ${theme["color-price-yellow"]};
                    color: ${theme["color-white"]};
                    width: fit-content;
                    padding: ${theme["spacing-s"]};
                    float: right;
                    height: 32px;
                    border-radius: 5px;
                }
            }

            &.unlimited {
                .plan {
                    color: ${theme["color-price-purple"]};
                    margin-bottom: ${theme["spacing-sm"]};
                }
            }

            .price {
                .text-largexlarge {
                    color: ${theme["color-black"]};
                }
            }

            .text-medium > div {
                margin-bottom: ${theme["spacing-m"]};

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        &.for-id {
            .item {
                height: auto;

                &.id8weeks {
                    .plan {
                        color: ${theme["color-blue"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }

                &.id16weeks {
                    .plan {
                        color: ${theme["color-price-yellow"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }

                &.id24weeks {
                    .plan {
                        color: ${theme["color-price-purple"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }
            }
        }
    }

    .subtext-container {
        display: block;
        flex-direction: row;
        justify-content: space-between;

        // Mobile view
        ${breakpoints(
            "flex-direction",
            "",
            [{ [size.px.sxs]: "column" }],
            "max-width"
        )};
        ${breakpoints(
            "align-items",
            "",
            [{ [size.px.sxs]: "center" }],
            "max-width"
        )};

        .text-medium {
            ${breakpoints(
                "margin-bottom",
                "",
                [{ [size.px.sxs]: "20px" }],
                "max-width"
            )};
            margin-bottom: 20px;
        }
    }

    > div,
    > h2 {
        margin-bottom: ${theme["spacing-l"]};

        &:last-child {
            margin-bottom: 0;
        }
    }

    h3 {
        font-weight: normal;
        font-size: 16px;
    }

    .non-mobile-view {
        ${breakpoints("display", "", [{ [size.px.xs]: "none" }], "max-width")};
    }

    .mobile-view {
        ${breakpoints(
            "display",
            "",
            [{ [(parseInt(size.px.xs) + 1).toString()]: "none" }],
            "min-width"
        )};

        &.for-id {
            .item {
                &.id8weeks {
                    .plan {
                        color: ${theme["color-blue"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }

                &.id16weeks {
                    .plan {
                        color: ${theme["color-price-yellow"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }

                &.id24weeks {
                    .plan {
                        color: ${theme["color-price-purple"]};
                        margin-bottom: ${theme["spacing-sm"]};
                    }
                }
            }
        }
    }
`;

const SCBookTrialButton = styled(SCButton)`
    background-color: ${theme["color-blue"]};
    color: ${theme["color-white"]};
    border: none;
`;
